import React, { useEffect } from 'react';
import Header from '../components/layout/Header';
import Footer from '../components/layout/Footer';
import CartDrawer from '../components/cart/CartDrawer';
import ProductCard from '../components/products/ProductCard';
import { Link } from 'react-router-dom';
import { products, categories, siteImages, steps, performanceBenefits, features, CONTACT_INFO } from '../data/mock';
import { ArrowRight, Truck, ShieldCheck, RotateCcw, Package, Wrench, Gauge, Zap, Award, CheckCircle2, Hammer } from 'lucide-react';

const featureIcons = [Wrench, Truck, RotateCcw, ShieldCheck];
const benefitIcons = [Hammer, Gauge, Package, CheckCircle2];

const HomePage = () => {
  const featuredProduct = products.find(p => p.slug === 'compact-cordless-drill-set-with-accessories') || products[0];
  const gridSlugs = ['multifunction-auto-pipe-wrench','180-in-1-precision-electric-screwdriver-set','advanced-waterproof-voltage-tester','professional-cordless-angle-grinder','precision-digital-clamp-meter-kit','laser-range-finder','jigsaw','rugged-waterproof-tool-storage-case'];
  const productsGrid = gridSlugs.map(s => products.find(p => p.slug === s)).filter(Boolean);

  useEffect(() => { document.title = 'Tool Supply Point | Industrial Hardware & Power Tools'; }, []);

  const stats = [
    { label: 'Tools in stock', value: '14+' },
    { label: 'Free USA shipping', value: '$0' },
    { label: 'Return window', value: '30 days' },
    { label: 'USPS delivery', value: '2-4 days' },
  ];

  return (
    <div className="min-h-screen bg-background text-dark-700">
      <Header />
      <CartDrawer />
      <main>
        {/* Hero — split panel */}
        <section className="relative bg-dark-900 overflow-hidden">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24 grid lg:grid-cols-12 gap-10 items-center">
            <div className="lg:col-span-6">
              <span className="inline-flex items-center gap-2 px-3 py-1.5 bg-athletic-600/15 text-athletic-400 text-xs font-bold uppercase tracking-[0.2em] rounded-sm mb-6 border border-athletic-600/30"><Zap className="w-3.5 h-3.5" /> Pro-grade gear, honest prices</span>
              <h1 className="font-display text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-[0.98] uppercase">Tools built to <span className="text-athletic-500">take a beating</span></h1>
              <p className="mt-6 text-lg text-dark-300 max-w-xl normal-case">Tool Supply Point stocks measuring gear, hand tools, cordless power tools and workshop storage that hold up on the job site and in the garage &mdash; shipped free across the USA.</p>
              <div className="mt-8 flex flex-wrap gap-3">
                <Link to="/shop" className="inline-flex items-center gap-2 rounded-sm bg-athletic-600 px-8 py-4 font-bold uppercase tracking-wide text-white transition hover:bg-athletic-700 shadow-button">Shop All Tools <ArrowRight className="w-4 h-4" /></Link>
                <Link to="/about" className="inline-flex items-center gap-2 rounded-sm border-2 border-white/25 px-8 py-4 font-bold uppercase tracking-wide text-white hover:bg-white/10 transition">Our Story</Link>
              </div>
            </div>
            <div className="lg:col-span-6 relative">
              <div className="relative rounded-sm overflow-hidden border-4 border-athletic-600 shadow-elevated">
                <img src={siteImages.heroMain} alt="Power tools on a workbench" className="w-full h-[300px] lg:h-[420px] object-cover" />
              </div>
              <div className="absolute -bottom-5 -left-5 hidden sm:flex items-center gap-3 bg-white rounded-sm shadow-elevated px-5 py-4">
                <div className="w-11 h-11 rounded-sm bg-athletic-600 text-white flex items-center justify-center"><Award className="w-5 h-5" /></div>
                <div><p className="font-display text-lg font-bold text-dark-900 uppercase leading-none">Job-site tough</p><p className="text-xs text-dark-500 mt-1">Tested for real work</p></div>
              </div>
            </div>
          </div>
          {/* Stat strip */}
          <div className="border-t border-dark-800 bg-dark-950">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 lg:grid-cols-4 divide-x divide-dark-800">
              {stats.map((s) => (
                <div key={s.label} className="py-6 px-4 text-center lg:text-left">
                  <p className="font-display text-3xl font-bold text-athletic-500 leading-none">{s.value}</p>
                  <p className="text-xs uppercase tracking-widest text-dark-400 mt-2">{s.label}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Categories — bold dark tiles */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-athletic-600 mb-2">Shop the racks</span><h2 className="font-display text-4xl font-bold text-dark-900 uppercase">Find your category</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold uppercase text-athletic-700 hover:gap-2.5 transition-all">All tools <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
              {categories.map((category) => (
                <Link key={category.id} to={`/shop?category=${category.slug}`} className="group relative rounded-sm overflow-hidden aspect-[3/4] border border-dark-200 hover:border-athletic-500 transition-all">
                  <img src={category.image} alt={category.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-dark-950/90 via-dark-950/30 to-transparent" />
                  <div className="absolute bottom-0 left-0 right-0 p-4">
                    <h3 className="font-display text-base font-bold text-white uppercase leading-tight">{category.name}</h3>
                    <p className="text-xs text-athletic-400 mt-1 font-semibold">{category.productCount} item{category.productCount > 1 ? 's' : ''} &rarr;</p>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>

        {/* Bestsellers */}
        <section className="bg-dark-50 py-16 sm:py-20 border-y border-dark-200">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex items-end justify-between mb-10">
              <div><span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-athletic-600 mb-2">Grab &amp; go</span><h2 className="font-display text-4xl font-bold text-dark-900 uppercase">Shop favorites</h2></div>
              <Link to="/shop" className="hidden sm:inline-flex items-center gap-1.5 text-sm font-bold uppercase text-athletic-700 hover:gap-2.5 transition-all">View all <ArrowRight className="w-4 h-4" /></Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">{productsGrid.map((p) => (<ProductCard key={p.id} product={p} />))}</div>
          </div>
        </section>

        {/* Flagship spotlight (image left) */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
            <div className="rounded-sm bg-dark-50 border border-dark-200 p-8"><img src={featuredProduct.image} alt={featuredProduct.name} className="w-full max-w-md mx-auto object-contain" /></div>
            <div>
              <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-athletic-600 mb-3">Kit of the week</span>
              <h2 className="font-display text-4xl font-bold text-dark-900 uppercase mb-4">{featuredProduct.name}</h2>
              <p className="text-dark-500 mb-5 max-w-lg leading-relaxed">{featuredProduct.description.slice(0, 200)}&hellip;</p>
              <p className="text-3xl font-bold text-athletic-600 mb-7">${featuredProduct.price.toFixed(2)}</p>
              <div className="flex flex-wrap gap-3">
                <Link to={`/product/${featuredProduct.slug}`} className="inline-flex items-center gap-2 px-8 py-4 bg-athletic-600 text-white rounded-sm font-bold uppercase tracking-wide hover:bg-athletic-700 transition shadow-button">Buy now</Link>
                <Link to="/shop" className="inline-flex items-center gap-2 px-8 py-4 border-2 border-dark-300 text-dark-800 rounded-sm font-bold uppercase tracking-wide hover:border-athletic-500 hover:text-athletic-600 transition">Browse more</Link>
              </div>
            </div>
          </div>
        </section>

        {/* Benefits band (orange) */}
        <section className="bg-gradient-athletic py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
              {performanceBenefits.map((b, i) => {
                const Icon = benefitIcons[i % benefitIcons.length];
                return (
                  <div key={b.id} className="text-center sm:text-left">
                    <div className="w-12 h-12 rounded-sm bg-white/15 flex items-center justify-center text-white mb-4 mx-auto sm:mx-0"><Icon className="w-6 h-6" /></div>
                    <h3 className="font-display text-xl font-bold text-white uppercase">{b.title}</h3>
                    <p className="text-white/85 text-sm mt-1">{b.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* How it works */}
        <section className="bg-white py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="text-center mb-12"><span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-athletic-600 mb-2">How it works</span><h2 className="font-display text-4xl font-bold text-dark-900 uppercase">From cart to job in four steps</h2></div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {steps.map((step) => (
                <div key={step.id} className="bg-dark-50 border border-dark-200 rounded-sm p-6"><span className="inline-flex items-center justify-center w-11 h-11 rounded-sm bg-athletic-600 text-white font-display font-bold text-lg">{step.id}</span><h3 className="font-display text-xl font-bold text-dark-900 mt-4 uppercase">{step.title}</h3><p className="text-dark-500 text-sm mt-2">{step.description}</p></div>
              ))}
            </div>
          </div>
        </section>

        {/* Trust / features strip */}
        <section className="bg-dark-900">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((f, i) => {
              const Icon = featureIcons[i % featureIcons.length];
              return (
                <div key={f.id} className="flex items-start gap-3">
                  <div className="w-11 h-11 rounded-sm bg-dark-800 text-athletic-500 flex items-center justify-center flex-shrink-0"><Icon className="w-5 h-5" /></div>
                  <div><p className="font-bold text-white text-sm uppercase">{f.title}</p><p className="text-dark-400 text-xs mt-0.5">{f.description}</p></div>
                </div>
              );
            })}
          </div>
        </section>

        {/* FAQ */}
        <section className="bg-dark-50 py-16 sm:py-20 border-t border-dark-200">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            <h2 className="font-display text-4xl font-bold text-dark-900 text-center mb-10 uppercase">Straight answers</h2>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {[
                { q: 'How fast will my order arrive?', a: `Orders ship via USPS within 1 business day and usually land in 2 to 4 business days. Need a status update? Call us at ${CONTACT_INFO.phone}.` },
                { q: 'Is shipping really free?', a: 'Every US order ships free via USPS Standard Shipping &mdash; no minimum spend, no surprise fees at checkout.' },
                { q: 'What if a tool is not right for me?', a: 'You have 30 days from delivery to start a return. Get in touch and our crew will walk you through it.' },
                { q: 'How do I reach a human?', a: `Our support team is on the line at ${CONTACT_INFO.phone} or by email at ${CONTACT_INFO.email}.` },
              ].map((faq, i) => (<div key={i} className="bg-white rounded-sm p-6 border border-dark-200"><h3 className="font-display text-xl font-bold text-dark-900 uppercase">{faq.q}</h3><p className="mt-2 text-dark-600">{faq.a}</p></div>))}
            </div>
            <div className="text-center mt-10"><Link to="/faq" className="inline-flex items-center gap-2 text-athletic-700 hover:text-athletic-800 font-bold uppercase">See all FAQs <ArrowRight className="w-4 h-4" /></Link></div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default HomePage;
